import {
  AttachedProgressBar,
  Box,
  Divider,
  ExposureLimitSideTypeEnum,
  Flex,
  MobileDataTableCardSurface,
  Text,
  WarningSeverityIcon,
  type CreditBlotterExposure,
  type MobileCardRendererProps,
} from '@talos/kyoko';
import { useCompactMobileCardLayout } from '../useCompactMobileCardLayout';
import {
  CREDIT_CURRENCY_COL_ID,
  CREDIT_EXPOSURE_DEFINITION_COL_ID,
  CREDIT_MARKET_ACCOUNT_COL_ID,
  CREDIT_USAGE_COL_ID,
  VISIBLE_COLUMN_RENDER_OPTIONS,
} from './mobileCreditColumns';
import { getRequiredCreditMetrics, type CreditMetric } from './mobileCreditMetric';
import {
  CreditCardPrimaryCell,
  CreditMetrics,
  CreditMetricValue,
  CreditMetricWrapper,
  CreditProgressBarLabel,
  CreditWarningCell,
} from './styles';

export const MOBILE_CREDIT_CARD_HEIGHT = 100;
export const COMPACT_MOBILE_CREDIT_CARD_HEIGHT = 56;

export function useMobileCreditCardLayout() {
  return useCompactMobileCardLayout(MOBILE_CREDIT_CARD_HEIGHT, COMPACT_MOBILE_CREDIT_CARD_HEIGHT);
}

export function MobileCreditCard({
  data,
  renderCell,
  onPress,
  compact = false,
}: MobileCardRendererProps<CreditBlotterExposure>) {
  const [exposureMetric, , creditUsageMetric] = getRequiredCreditMetrics(renderCell);
  const identity = getCreditCardIdentity(data);

  return (
    <MobileDataTableCardSurface
      type="button"
      data-testid="mobile-credit-card"
      onClick={onPress}
      $background="inherit"
      $compact={compact}
    >
      <Flex alignItems="center" gap="spacing8" overflow="hidden">
        <CreditCardPrimaryCell data-testid={identity.primaryTestId} overflow="hidden">
          {renderCell(identity.primaryColId, VISIBLE_COLUMN_RENDER_OPTIONS)}
        </CreditCardPrimaryCell>
        {!compact ? (
          <Box data-testid={identity.secondaryTestId} color="colorTextSubtle">
            {renderCell(identity.secondaryColId, VISIBLE_COLUMN_RENDER_OPTIONS)}
          </Box>
        ) : null}
        {data.creditUsageWarning ? (
          <CreditWarningCell data-testid="mobile-credit-card-credit-usage-warning">
            <WarningSeverityIcon severity={data.creditUsageWarning.severity} data-testid="warning-bar" />
          </CreditWarningCell>
        ) : null}
        <CreditMetricItem metric={exposureMetric} compact={compact} alignRight />
      </Flex>

      {!compact ? <Divider color="backgroundDivider" /> : null}

      {!compact ? (
        <CreditMetrics>
          <CreditMetricItem metric={creditUsageMetric} progressBar />
        </CreditMetrics>
      ) : null}
      {compact && data.effectiveSide?.usage ? (
        <AttachedProgressBar
          value={data.effectiveSide.usage.usage.toFixed()}
          max={1}
          color={getCreditUsageProgressColor(data)}
        />
      ) : null}
    </MobileDataTableCardSurface>
  );
}

function CreditMetricItem({
  metric,
  compact = false,
  progressBar = false,
  alignRight = false,
}: {
  metric: CreditMetric;
  compact?: boolean;
  progressBar?: boolean;
  alignRight?: boolean;
}) {
  const showProgressBar = !compact && progressBar && metric.colId === CREDIT_USAGE_COL_ID;

  return (
    <CreditMetricWrapper $compact={compact} $progressBar={showProgressBar} $alignRight={alignRight}>
      {showProgressBar ? (
        <CreditProgressBarLabel color="colorTextSubtle" fontSize="fontSizeSm">
          {metric.label}
        </CreditProgressBarLabel>
      ) : (
        <Text color="colorTextSubtle" fontSize="fontSizeSm">
          {metric.label}
        </Text>
      )}
      <CreditMetricValue data-testid={metric.testId} color="colorTextImportant" $progressBar={showProgressBar}>
        {metric.value}
      </CreditMetricValue>
    </CreditMetricWrapper>
  );
}

function getCreditCardIdentity(data: CreditBlotterExposure) {
  if (data.Currency) {
    return {
      primaryColId: CREDIT_CURRENCY_COL_ID,
      primaryTestId: 'mobile-credit-card-asset',
      secondaryColId: CREDIT_EXPOSURE_DEFINITION_COL_ID,
      secondaryTestId: 'mobile-credit-card-exposure-definition',
    };
  }

  return {
    primaryColId: CREDIT_MARKET_ACCOUNT_COL_ID,
    primaryTestId: 'mobile-credit-card-market-account',
    secondaryColId: CREDIT_CURRENCY_COL_ID,
    secondaryTestId: 'mobile-credit-card-asset',
  };
}

function getCreditUsageProgressColor(data: CreditBlotterExposure) {
  if (data.effectiveSide?.ExposureSide === ExposureLimitSideTypeEnum.Short) {
    return 'colors.red.lighten';
  }

  if (data.effectiveSide?.ExposureSide === ExposureLimitSideTypeEnum.Long) {
    return 'colors.green.lighten';
  }

  return 'colors.blue.lighten';
}
